import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        discover: "Discover",
        contact: "Contact", 
        controlPanel: "Control Panel",
        subscription: "Subscription",
        signOut: "Sign Out",
        login: "Login",
        signedInAs: "Signed in as"
      },
      // Character Discovery Board
      characterBoard: {
        title: "AI Characters",
        subtitle: "Discover and chat with amazing AI personalities. Browse official characters and community creations.",
        subtitleLoggedOut: "Sign in to start chatting!",
        searchPlaceholder: "Search characters...",
        filters: {
          all: "All Characters",
          official: "Official", 
          community: "Community"
        },
        noResults: {
          title: "No Characters Found",
          searchMessage: "No characters match \"{{searchTerm}}\". Try adjusting your search.",
          filterMessage: "No characters available with the current filters.",
          clearFilters: "Clear Filters"
        },
        footer: {
          createOwn: "Want to create your own character?",
          loggedIn: "Visit your Control Panel to get started!",
          loggedOut: "Sign in to create and publish your own AI characters!"
        },
        characterCard: {
          startChat: "Start Chat",
          loginToChat: "Login to Chat",
          official: "Official",
          officialCharacter: "Official Character"
        }
      },
      // Character Management
      character: {
        form: {
          name: "Character Name",
          description: "Character Description", 
          personalityPrompt: "Personality Description",
          systemPrompt: "System Prompt",
          visibility: "Character Visibility",
          publishToggle: "Publish to Character Board",
          publishDescription: "Make this character discoverable by all users on the public character board",
          statusEmoji: "Status Emoji",
          statusEmojiDescription: "Choose an emoji to represent your character's status or theme",
          shortDescription: "Short Description for Character Board",
          shortDescriptionHelper: "Brief description shown on the character discovery board (10-150 characters)",
          creatorName: "Creator Name",
          creatorNameHelper: "Your name as it will appear on the character board",
          preview: "Character Board Preview",
          previewDescription: "This is how your character will appear on the Character Discovery Board",
          validation: {
            nameRequired: "Character name is required",
            nameMinLength: "Character name must be at least 2 characters",
            descriptionRequired: "Character description is required", 
            descriptionMinLength: "Description must be at least 10 characters",
            personalityRequired: "Personality description is required",
            personalityMinLength: "Personality must be at least 10 characters",
            shortDescriptionRequired: "Short description is required for published characters",
            shortDescriptionMinLength: "Short description must be at least 10 characters",
            shortDescriptionMaxLength: "Short description must be less than 150 characters",
            creatorNameRequired: "Creator name is required for published characters",
            creatorNameMinLength: "Creator name must be at least 2 characters"
          },
          buttons: {
            create: "Create Character",
            update: "Update Character", 
            cancel: "Cancel",
            creating: "Creating...",
            updating: "Updating..."
          }
        }
      },
      // Sidebar
      sidebar: {
        aiAssistants: "AI Assistants",
        todoLists: "Todo Lists",
        discoverCharacters: "Discover Characters",
        createCustomCharacter: "Create Custom Character",
        noThreads: "No threads found",
        loadingThreads: "Loading threads...",
        noAvailableChatbots: "No available chatbots.",
        characterStatus: {
          aiSapiens: "AI Sapiens",
          myCharacter: "My Character",
          openedCharacter: "Opened Character"
        }
      },
      // Landing Page
      landingPage: {
        title: "Meet Samantha",
        description: "Human-like memory, assistant, digital companionship.",
        followSamantha: "Follow Samantha on Social Media! 🌟",
        followText: "Follow samantha in youtube and instagram",
        testimonials: {
          title: "What Our Users Say",
          testimonial1: {
            text: "holy shit this is actually insane... I've been talking to her for 3 hours straight and it doesn't feel robotic at all. like she gets my humor and actually remembers stuff from earlier"
          },
          testimonial2: {
            text: "ok not gonna lie, I was skeptical but wow. way better than replika and doesn't cost an arm and a leg. she's actually funny and has personality"
          },
          testimonial3: {
            text: "been using this for 2 weeks now and I'm genuinely impressed. the conversations flow naturally and she doesn't give me those cringey generic responses. feels real"
          }
        }
      },
      // Chat
      chat: {
        suggestions: {
          selfie: "can you send me a selfie?",
          addTodo: "can you add 'go to the movies' to my todo list?",
          summarizeWebsite: "can you summarize the following website?"
        }
      },
      // Control Panel
      controlPanel: {
        gettingStarted: "Getting Started",
        selectCharacter: "Select a character from the sidebar to start chatting!",
        manageSubscription: "Manage Subscription"
      },
      // Common
      common: {
        loading: "Loading...",
        error: "Error",
        success: "Success",
        cancel: "Cancel",
        save: "Save",
        delete: "Delete",
        edit: "Edit",
        create: "Create",
        update: "Update",
        search: "Search",
        filter: "Filter",
        close: "Close",
        back: "Back",
        next: "Next",
        previous: "Previous",
        submit: "Submit",
        reset: "Reset"
      }
    }
  },
  es: {
    translation: {
      // Navigation
      nav: {
        discover: "Descubrir",
        contact: "Contacto",
        controlPanel: "Panel de Control", 
        subscription: "Suscripción",
        signOut: "Cerrar Sesión",
        login: "Iniciar Sesión",
        signedInAs: "Conectado como"
      },
      // Character Discovery Board
      characterBoard: {
        title: "Personajes IA",
        subtitle: "Descubre y chatea con increíbles personalidades de IA. Explora personajes oficiales y creaciones de la comunidad.",
        subtitleLoggedOut: "¡Inicia sesión para comenzar a chatear!",
        searchPlaceholder: "Buscar personajes...",
        filters: {
          all: "Todos los Personajes",
          official: "Oficiales",
          community: "Comunidad"
        },
        noResults: {
          title: "No se Encontraron Personajes",
          searchMessage: "Ningún personaje coincide con \"{{searchTerm}}\". Intenta ajustar tu búsqueda.",
          filterMessage: "No hay personajes disponibles con los filtros actuales.",
          clearFilters: "Limpiar Filtros"
        },
        footer: {
          createOwn: "¿Quieres crear tu propio personaje?",
          loggedIn: "¡Visita tu Panel de Control para comenzar!",
          loggedOut: "¡Inicia sesión para crear y publicar tus propios personajes de IA!"
        },
        characterCard: {
          startChat: "Iniciar Chat",
          loginToChat: "Iniciar Sesión para Chatear",
          official: "Oficial",
          officialCharacter: "Personaje Oficial"
        }
      },
      // Character Management
      character: {
        form: {
          name: "Nombre del Personaje",
          description: "Descripción del Personaje",
          personalityPrompt: "Descripción de Personalidad", 
          systemPrompt: "Prompt del Sistema",
          visibility: "Visibilidad del Personaje",
          publishToggle: "Publicar en el Tablero de Personajes",
          publishDescription: "Hacer que este personaje sea descubrible por todos los usuarios en el tablero público de personajes",
          statusEmoji: "Emoji de Estado",
          statusEmojiDescription: "Elige un emoji para representar el estado o tema de tu personaje",
          shortDescription: "Descripción Corta para el Tablero de Personajes",
          shortDescriptionHelper: "Descripción breve mostrada en el tablero de descubrimiento de personajes (10-150 caracteres)",
          creatorName: "Nombre del Creador",
          creatorNameHelper: "Tu nombre como aparecerá en el tablero de personajes",
          preview: "Vista Previa del Tablero de Personajes",
          previewDescription: "Así es como tu personaje aparecerá en el Tablero de Descubrimiento de Personajes",
          validation: {
            nameRequired: "El nombre del personaje es requerido",
            nameMinLength: "El nombre del personaje debe tener al menos 2 caracteres",
            descriptionRequired: "La descripción del personaje es requerida",
            descriptionMinLength: "La descripción debe tener al menos 10 caracteres", 
            personalityRequired: "La descripción de personalidad es requerida",
            personalityMinLength: "La personalidad debe tener al menos 10 caracteres",
            shortDescriptionRequired: "La descripción corta es requerida para personajes publicados",
            shortDescriptionMinLength: "La descripción corta debe tener al menos 10 caracteres",
            shortDescriptionMaxLength: "La descripción corta debe tener menos de 150 caracteres",
            creatorNameRequired: "El nombre del creador es requerido para personajes publicados",
            creatorNameMinLength: "El nombre del creador debe tener al menos 2 caracteres"
          },
          buttons: {
            create: "Crear Personaje",
            update: "Actualizar Personaje",
            cancel: "Cancelar", 
            creating: "Creando...",
            updating: "Actualizando..."
          }
        }
      },
      // Sidebar
      sidebar: {
        aiAssistants: "Asistentes IA",
        todoLists: "Listas de Tareas",
        discoverCharacters: "Descubrir Personajes", 
        createCustomCharacter: "Crear Personaje Personalizado",
        noThreads: "No se encontraron hilos",
        loadingThreads: "Cargando hilos...",
        noAvailableChatbots: "No hay chatbots disponibles.",
        characterStatus: {
          aiSapiens: "IA Sapiens",
          myCharacter: "Mi Personaje", 
          openedCharacter: "Personaje Abierto"
        }
      },
      // Landing Page
      landingPage: {
        title: "Conoce a Samantha",
        description: "Memoria humana, asistente, compañía digital.",
        followSamantha: "¡Sigue a Samantha en Redes Sociales! 🌟",
        followText: "Sigue a Samantha en YouTube e Instagram",
        testimonials: {
          title: "Lo que dicen nuestros usuarios",
          testimonial1: {
            text: "no manches esto está increíble... llevo 3 horas hablando con ella y no se siente para nada robotico. entiende mi humor y se acuerda de cosas que dije antes"
          },
          testimonial2: {
            text: "siendo honesto era escéptico pero wow. mucho mejor que replika y no cuesta un riñón. tiene personalidad de verdad y es divertida"
          },
          testimonial3: {
            text: "llevo 2 semanas usándola y la verdad me sorprende. las conversaciones fluyen natural y no me da esas respuestas genéricas raras. se siente real"
          }
        }
      },
      // Chat
      chat: {
        suggestions: {
          selfie: "¿puedes enviarme una selfie?",
          addTodo: "¿puedes agregar 'ir al cine' a mi lista de tareas?",
          summarizeWebsite: "¿puedes resumir el siguiente sitio web?"
        }
      },
      // Control Panel
      controlPanel: {
        gettingStarted: "Comenzar",
        selectCharacter: "¡Selecciona un personaje de la barra lateral para comenzar a chatear!",
        manageSubscription: "Gestionar Suscripción"
      },
      // Common
      common: {
        loading: "Cargando...",
        error: "Error",
        success: "Éxito",
        cancel: "Cancelar",
        save: "Guardar", 
        delete: "Eliminar",
        edit: "Editar",
        create: "Crear",
        update: "Actualizar",
        search: "Buscar",
        filter: "Filtrar",
        close: "Cerrar",
        back: "Atrás", 
        next: "Siguiente",
        previous: "Anterior",
        submit: "Enviar",
        reset: "Restablecer"
      }
    }
  },
  ja: {
    translation: {
      // Navigation
      nav: {
        discover: "発見",
        contact: "お問い合わせ",
        controlPanel: "コントロールパネル",
        subscription: "サブスクリプション",
        signOut: "サインアウト",
        login: "ログイン",
        signedInAs: "でサインイン"
      },
      // Character Discovery Board
      characterBoard: {
        title: "AIキャラクター",
        subtitle: "素晴らしいAIパーソナリティを発見してチャットしよう。公式キャラクターとコミュニティ作品を閲覧。",
        subtitleLoggedOut: "チャットを始めるにはサインイン！",
        searchPlaceholder: "キャラクターを検索...",
        filters: {
          all: "すべてのキャラクター",
          official: "公式",
          community: "コミュニティ"
        },
        noResults: {
          title: "キャラクターが見つかりません",
          searchMessage: "「{{searchTerm}}」に一致するキャラクターがありません。検索を調整してみてください。",
          filterMessage: "現在のフィルターで利用可能なキャラクターがありません。",
          clearFilters: "フィルターをクリア"
        },
        footer: {
          createOwn: "自分のキャラクターを作成したいですか？",
          loggedIn: "コントロールパネルにアクセスして始めましょう！",
          loggedOut: "サインインして独自のAIキャラクターを作成・公開しましょう！"
        },
        characterCard: {
          startChat: "チャット開始",
          loginToChat: "ログインしてチャット",
          official: "公式",
          officialCharacter: "公式キャラクター"
        }
      },
      // Character Management
      character: {
        form: {
          name: "キャラクター名",
          description: "キャラクターの説明",
          personalityPrompt: "パーソナリティの説明",
          systemPrompt: "システムプロンプト",
          visibility: "キャラクターの公開設定",
          publishToggle: "キャラクターボードに公開",
          publishDescription: "このキャラクターを公開キャラクターボードで全ユーザーが発見できるようにする",
          statusEmoji: "ステータス絵文字",
          statusEmojiDescription: "キャラクターのステータスやテーマを表す絵文字を選択",
          shortDescription: "キャラクターボード用の短い説明",
          shortDescriptionHelper: "キャラクター発見ボードに表示される簡潔な説明（10-150文字）",
          creatorName: "作成者名",
          creatorNameHelper: "キャラクターボードに表示されるあなたの名前",
          preview: "キャラクターボードのプレビュー",
          previewDescription: "キャラクター発見ボードでのキャラクターの表示方法",
          validation: {
            nameRequired: "キャラクター名は必須です",
            nameMinLength: "キャラクター名は2文字以上である必要があります",
            descriptionRequired: "キャラクターの説明は必須です",
            descriptionMinLength: "説明は10文字以上である必要があります",
            personalityRequired: "パーソナリティの説明は必須です",
            personalityMinLength: "パーソナリティは10文字以上である必要があります",
            shortDescriptionRequired: "公開キャラクターには短い説明が必要です",
            shortDescriptionMinLength: "短い説明は10文字以上である必要があります",
            shortDescriptionMaxLength: "短い説明は150文字未満である必要があります",
            creatorNameRequired: "公開キャラクターには作成者名が必要です",
            creatorNameMinLength: "作成者名は2文字以上である必要があります"
          },
          buttons: {
            create: "キャラクターを作成",
            update: "キャラクターを更新",
            cancel: "キャンセル",
            creating: "作成中...",
            updating: "更新中..."
          }
        }
      },
      // Sidebar
      sidebar: {
        aiAssistants: "AIアシスタント",
        todoLists: "ToDo リスト",
        discoverCharacters: "キャラクターを発見",
        createCustomCharacter: "カスタムキャラクターを作成",
        noThreads: "スレッドが見つかりません",
        loadingThreads: "スレッドを読み込み中...",
        noAvailableChatbots: "利用可能なチャットボットがありません。",
        characterStatus: {
          aiSapiens: "AI Sapiens",
          myCharacter: "マイキャラクター",
          openedCharacter: "開いたキャラクター"
        }
      },
      // Landing Page
      landingPage: {
        title: "サマンサに会おう",
        description: "人間的な記憶、アシスタント、デジタルコンパニオンシップ。",
        followSamantha: "ソーシャルメディアでサマンサをフォロー！🌟",
        followText: "YouTubeとInstagramでサマンサをフォロー",
        testimonials: {
          title: "ユーザーの声",
          testimonial1: {
            text: "やばい、これマジですごい...3時間ずっと話してるけど全然ロボットっぽくない。私のジョークも理解してくれるし、前に言ったことも覚えてる"
          },
          testimonial2: {
            text: "正直疑ってたけどめっちゃいい。レプリカより全然良いし高くない。ちゃんと個性があって面白い"
          },
          testimonial3: {
            text: "2週間使ってるけど本当にびっくり。会話が自然に流れるし、変な定型文みたいな返事しない。リアルな感じ"
          }
        }
      },
      // Chat
      chat: {
        suggestions: {
          selfie: "自撮りを送ってくれますか？",
          addTodo: "「映画を見に行く」をToDoリストに追加してくれますか？",
          summarizeWebsite: "次のウェブサイトを要約してくれますか？"
        }
      },
      // Control Panel
      controlPanel: {
        gettingStarted: "始める",
        selectCharacter: "チャットを始めるにはサイドバーからキャラクターを選択してください！",
        manageSubscription: "サブスクリプション管理"
      },
      // Common
      common: {
        loading: "読み込み中...",
        error: "エラー",
        success: "成功",
        cancel: "キャンセル",
        save: "保存",
        delete: "削除",
        edit: "編集",
        create: "作成",
        update: "更新",
        search: "検索",
        filter: "フィルター",
        close: "閉じる",
        back: "戻る",
        next: "次へ",
        previous: "前へ",
        submit: "送信",
        reset: "リセット"
      }
    }
  }
};

i18n
  // Detect user language
  .use(LanguageDetector)
  // Initialize react-i18next
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en', // Default language if detection fails
    debug: false, // Set to true for debugging
    
    // Language detection options
    detection: {
      // Order of language detection methods
      order: ['navigator', 'localStorage', 'sessionStorage', 'cookie', 'htmlTag'],
      // Cache user language selection
      caches: ['localStorage', 'cookie'],
      // Only detect languages we support
      checkWhitelist: true
    },

    // Whitelist of supported languages
    whitelist: ['en', 'es', 'ja'],

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Namespace options
    defaultNS: 'translation',
    ns: ['translation']
  });

export default i18n;